import styled from "styled-components";
import { FadeImage } from "../../general/FadeImage";
import { colors, easing, fonts, media, radii, shadows } from "../../../theme";

export const CardContainer = styled.article`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid
    ${({ $expanded }) =>
      $expanded ? colors.primary : "rgba(210, 180, 140, 0.45)"};
  border-radius: ${radii.lg};
  background-color: ${colors.backgroundSecondary};
  box-shadow: ${({ $expanded }) => ($expanded ? shadows.md : "none")};
  transition: transform 0.5s ${easing.out}, box-shadow 0.5s ${easing.out},
    border-color 0.3s ease;

  ${media.hoverable} {
    &:hover {
      transform: ${({ $expanded }) =>
        $expanded ? "none" : "translateY(-6px)"};
      box-shadow: ${shadows.lg};
      border-color: ${({ $expanded }) =>
        $expanded ? colors.primary : "rgba(255, 195, 11, 0.7)"};
    }
  }
`;

export const Summary = styled.div`
  cursor: pointer;
`;

export const ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background-color: ${colors.backgroundTernary};
`;

export const Image = styled(FadeImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;

  ${media.hoverable} {
    ${CardContainer}:hover & {
      transform: scale(1.06);
    }
  }
`;

export const DiscountBadge = styled.span`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: ${radii.pill};
  background-color: ${colors.error};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 16px rgba(255, 76, 76, 0.35);
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 1.25rem 1.25rem 1.35rem;
`;

export const Title = styled.h3`
  font-size: 1.25rem;
  line-height: 1.25;
  color: ${colors.textMain};
`;

export const Description = styled.p`
  font-size: 0.92rem;
  line-height: 1.55;
  color: ${colors.textMuted};

  ${({ $expanded }) =>
    !$expanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const SizeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

export const SizeChip = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: ${radii.pill};
  background-color: ${colors.backgroundQuaternary};
  color: ${colors.textMain};
  font-size: 0.75rem;
  font-weight: 600;
`;

export const PriceRange = styled.span`
  font-family: ${fonts.heading};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.textSecondary};
`;

export const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.25rem;
  padding: 0.65rem 1rem;
  border: 1px solid rgba(210, 180, 140, 0.8);
  border-radius: ${radii.pill};
  background-color: ${({ $expanded }) =>
    $expanded ? colors.primary : colors.backgroundTernary};
  color: ${colors.textMain};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease, border-color 0.3s ease;

  svg {
    transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
    transition: transform 0.4s ${easing.out};
  }

  &:hover {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
  }
`;

// Animating grid rows between 0fr and 1fr gives a smooth height transition
// without measuring the content.
export const Collapse = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.5s ${easing.out};
`;

export const CollapseClip = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const CollapseContent = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  border-top: 1px solid rgba(210, 180, 140, 0.45);
  background-color: ${colors.backgroundMain};
`;

export const VariantsTitle = styled.h4`
  margin-bottom: 0.5rem;
  font-family: ${fonts.body};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.textMuted};
`;

export const ProductVariant = styled.div`
  display: grid;
  grid-template-columns: 3.25rem 1fr auto;
  align-items: center;
  gap: 0.85rem;
  padding: 0.6rem 0;
  border-bottom: 1px dashed rgba(210, 180, 140, 0.6);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? "none" : "translateY(0.5rem)")};
  transition: opacity 0.4s ease, transform 0.5s ${easing.out};
  transition-delay: ${({ $open, $index }) =>
    $open ? 0.15 + $index * 0.06 : 0}s;

  &:last-child {
    border-bottom: none;
  }
`;

export const VariantImage = styled.img`
  width: 3.25rem;
  height: 3.25rem;
  object-fit: cover;
  border-radius: 0.65rem;
  border: 1px solid rgba(210, 180, 140, 0.6);
`;

export const VariantSize = styled.span`
  font-weight: 600;
`;

export const VariantPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 0.95rem;
  font-weight: 600;
`;

export const OldPrice = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${colors.textMuted};
  text-decoration: line-through;
`;

export const NewPrice = styled.span`
  color: ${colors.quinary};
  font-weight: 700;
`;
